/**
 * RoleSelect — selector de roles de usuario con descripción de cada rol y avisos.
 *
 * Hallazgo 40 del QA manual: el diálogo ofrecía los 8 roles sin explicar qué hace cada
 * uno y era fácil marcar roles de más (un usuario de campo terminó con SUPER_ADMIN y
 * ADMIN, lo que además le abrió consultas libres a la base). Aquí cada opción muestra su
 * descripción y se avisa al marcar SUPER_ADMIN/ADMIN o demasiados roles.
 */
import { Dropdown, Option, Text, tokens } from '@fluentui/react-components';
import type { Role } from '../../types/api.types';
import { useT } from '../../i18n';

export const ASSIGNABLE_ROLE_LIST: Role[] = [
  'SUPER_ADMIN', 'ADMIN', 'AGRONOMIST', 'SUPERVISOR', 'WAREHOUSE_MANAGER', 'FIELD_WORKER', 'AUDITOR', 'INVESTOR', 'HR', 'GUARD',
];

/** A partir de cuántos roles se avisa que probablemente sobran. */
export const MANY_ROLES_THRESHOLD = 4;

/** Avisos aplicables a una selección de roles (pura, para poder testearla). */
export function roleSelectionWarnings(roles: Role[]): Array<'superAdmin' | 'admin' | 'many'> {
  const out: Array<'superAdmin' | 'admin' | 'many'> = [];
  if (roles.includes('SUPER_ADMIN')) out.push('superAdmin');
  else if (roles.includes('ADMIN')) out.push('admin');
  if (roles.length >= MANY_ROLES_THRESHOLD) out.push('many');
  return out;
}

/** Roles que ofrece una invitación por link a quien no es Super Admin (el servidor rechaza SUPER_ADMIN de otros). */
export const INVITABLE_ROLE_LIST: Role[] = ASSIGNABLE_ROLE_LIST.filter(r => r !== 'SUPER_ADMIN');

export function RoleSelect({ roles, onChange, options = ASSIGNABLE_ROLE_LIST }: {
  roles: Role[];
  onChange: (roles: Role[]) => void;
  options?: Role[];
}) {
  const t = useT();
  const descs = t.demoLogin.roleDesc as Record<string, string>;
  const warnings = roleSelectionWarnings(roles);

  return (
    <>
      <Dropdown
        multiselect
        selectedOptions={roles}
        value={roles.map(r => t.settings.roles[r] ?? r).join(', ')}
        onOptionSelect={(_, d) => onChange(d.selectedOptions as Role[])}
        style={{ width: '100%' }}
      >
        {options.map(r => (
          <Option key={r} value={r} text={t.settings.roles[r] ?? r}>
            <span style={{ display: 'flex', flexDirection: 'column' }}>
              <span>{t.settings.roles[r] ?? r}</span>
              <span style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>{descs[r]}</span>
            </span>
          </Option>
        ))}
      </Dropdown>
      {warnings.map(w => (
        <Text
          key={w}
          role="alert"
          style={{
            fontSize: '12px', display: 'block', marginTop: '4px',
            color: w === 'many' ? tokens.colorNeutralForeground3 : tokens.colorStatusWarningForeground1,
          }}
        >
          {w === 'superAdmin' ? t.users.roleWarnSuperAdmin : w === 'admin' ? t.users.roleWarnAdmin : t.users.roleWarnMany}
        </Text>
      ))}
    </>
  );
}
